import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { YearEndStatus } from '../../../types/finance';
import { formatStatementPaisa } from '../../../utils/paisa';
import ConfirmationDialog, { ConfirmFacts } from '../../common/ConfirmationDialog';
import { SectionCard } from '../../common/SectionCard';

export const fyLabel = (fy: number) => `FY ${fy - 1}–${String(fy).slice(-2)}`;

/**
 * Year-end close for one fiscal year: the year's result moves into
 * accumulated profit, drawings into capital, and every month locks.
 * Possible once all twelve months are closed.
 */
const YearEndCard: React.FC<{ fiscalYear: number; refreshKey: number; editable: boolean; onClosed: () => void }> = ({
  fiscalYear, refreshKey, editable, onClosed,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<YearEndStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await financeService.getYearEnd(fiscalYear));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the year-end status.');
    }
  }, [fiscalYear]);

  useEffect(() => { setStatus(null); load(); }, [load, refreshKey]);

  const close = async () => {
    setBusy(true);
    try {
      const r = await financeService.closeYear(fiscalYear);
      enqueueSnackbar(`${fyLabel(fiscalYear)} closed${r.entryNo ? ` — ${r.entryNo}` : ''}`, { variant: 'success' });
      setConfirming(false);
      onClosed();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'The year could not close.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const result = status && (status.profitPaisa >= 0 ? 'Profit for the year' : 'Loss for the year');

  return (
    <SectionCard title="Year-end close" sx={{ mt: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {!status && !error && <Skeleton height={56} />}
      {status && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            {status.closed ? (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                <Typography variant="body2" fontWeight={600}>
                  {fyLabel(fiscalYear)} is closed{status.entryNo ? ` — closing entry ${status.entryNo}` : ' (nothing to move)'}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" fontWeight={600}>
                {!status.ended
                  ? `The year ends on 30 June ${fiscalYear}; it can close after that, once every month is closed.`
                  : status.openMonths
                    ? `${status.openMonths} month${status.openMonths === 1 ? '' : 's'} still open — close every month first.`
                    : 'Every month is closed. The year is ready to close.'}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {result}: <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: status.profitPaisa < 0 ? 'error.main' : 'text.primary' }}>
                {formatStatementPaisa(status.profitPaisa)}
              </Box>
              {!status.closed && ' — moves into accumulated profit when the year closes.'}
            </Typography>
          </Box>
          {editable && !status.closed && (
            <Button variant="contained" disabled={!status.canClose} onClick={() => setConfirming(true)} sx={{ flexShrink: 0 }}>
              Close the year
            </Button>
          )}
        </Stack>
      )}

      <ConfirmationDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={close}
        loading={busy}
        title={`Close ${fyLabel(fiscalYear)}?`}
        message="One closing entry, dated the year's last day, empties every income and expense account into accumulated profit and moves drawings into capital. Then every month of the year locks for good."
        confirmText="Close the year"
        confirmColor="primary"
      >
        {status && (
          <ConfirmFacts items={[
            { label: result, value: formatStatementPaisa(status.profitPaisa) },
            { label: 'Moves into', value: '3200 Accumulated profit' },
            { label: 'Drawings move into', value: '3100 Capital' },
            { label: 'Afterwards', value: 'All 12 months locked' },
          ]} />
        )}
      </ConfirmationDialog>
    </SectionCard>
  );
};

export default YearEndCard;
